// src/Pages/TestTop.js — トップページ
import React from "react";
import { Link } from "react-router-dom";
import { Button, Chevron, Check, Container, SectionTitle } from "../Components/ui";
import { useBrandMap, useProducts } from "../api/products";
import { questions } from "../Components/Wizard/questions";

const PROBLEMS = [
  {
    title: "カメラ選びが難しい",
    text: "用途や設置環境に合った機種を自分で決めようにも、何を基準に選ぶかがわからない。",
  },
  {
    title: "比較が大変",
    text: "せっかく条件を絞り込んでも、各社で類似商品がありすぎて最終的な決め方がわからないこともよく聞くお悩みです。",
  },
  {
    title: "工程がバラバラ",
    text: "カメラの手配・設置工事・アフターサポートなどの作業フローが分断されていて、設置までに多くの業者を介することになり、さまざまなコストが嵩むことになるのが現状です。",
  },
  {
    title: "導入後の不安",
    text: "トラブル発生時にその原因がわからず、初期対応遅れの可能性とそれによる一時的にでも不安定なセキュリティに。",
  },
];

const STEPS = [
  {
    title: "直感で、最適を選ぶ",
    text: "カメラ選びはシンプルに。数問の質問に直感でタップしていくだけで、最適なカメラをご提案。選択に迷わず、すぐに結果が手に入ります。",
  },
  {
    title: "ワンタップで情報送信",
    text: "結果はそのままコピー＆ペースト。余計な入力や説明は一切不要です。気に入った内容を、お問い合わせフォームに貼り付けて送信してください。",
  },
  {
    title: "導入サポート",
    text: "ご連絡を受け次第、こちらで最適な業者を手配。現地調査の段取りもすぐに進めます。お客様の手間はゼロ。業務に集中していただけます。",
  },
  {
    title: "現地調査で最終確認",
    text: "スタッフが設置予定の場所へ伺い、現地の条件を確認します。配線や視野、取り付けの可否などを事前にチェック。導入前のズレを未然に防ぎます。",
  },
  {
    title: "見積もりをご案内",
    text: "調査結果に基づき、内容とコストを最適化したお見積もりをご提示。納得感のある提案を心がけていますので、ご不明点はいつでもご相談ください。",
  },
  {
    title: "設置工事",
    text: "経験豊富なスタッフが、丁寧かつスムーズに施工を実施。完了後はすぐに使える状態でお引き渡し。不具合やご相談があれば、いつでもお気軽にご連絡ください。",
  },
];

const QUESTION_COUNT = questions.length;

export default function TestTop() {
  return (
    <>
      <Hero />
      <Problems />
      <Flow />
      <Makers />
      <Closing />
    </>
  );
}

/* ───────── ファーストビュー ───────── */
function Hero() {
  return (
    <section className="overflow-hidden bg-white">
      <Container className="flex flex-col items-center pt-14 text-center md:pt-24">
        <h1 className="text-[31px] font-bold leading-[1.25] tracking-tight sm:text-[48px] md:text-[64px] md:leading-[1.18]">
          複雑なセキュリティを、
          <br />
          シンプルに。
        </h1>
        <p className="mt-5 max-w-[720px] text-[17px] leading-relaxed text-ink-2 md:text-[21px]">
          {QUESTION_COUNT}つの質問に答えるだけで、条件に合う防犯カメラが見つかります。
          <br className="hidden md:inline" />
          選定から設置まで、まとめてご相談いただけます。
        </p>
        <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:gap-7">
          <Button to="/wizard" className="!min-h-[50px] !px-7">
            無料で診断する
          </Button>
          <Link to="/contact" className="flex min-h-11 items-center gap-1 text-[17px] text-tint hover:text-tint-dark">
            お問い合わせ
            <Chevron className="h-3 w-2" />
          </Link>
        </div>

        <PhoneMock />
      </Container>
    </section>
  );
}

function PhoneMock() {
  const q = questions.find((x) => x.key === "connectivity") ?? questions[0];
  const opts = q.options.map((o) => (typeof o === "string" ? o : o.label));
  return (
    <div
      aria-hidden="true"
      className="mt-12 h-[440px] w-[300px] overflow-hidden rounded-t-[46px] border-[11px] border-b-0 border-ink bg-fill-2 px-4 pt-6 text-left md:mt-16 md:h-[520px] md:w-[340px] md:rounded-t-[52px] md:border-[12px]"
    >
      <div className="flex items-center justify-between text-[15px]">
        <span className="text-tint">‹ 戻る</span>
        <span className="text-[13px] text-ink-2">3 / {QUESTION_COUNT}</span>
      </div>
      <div className="mt-3 text-[24px] font-bold leading-snug md:text-[26px]">
        インターネットの
        <br />
        状況は？
      </div>
      <div className="mt-2 truncate text-[12px] text-ink-2">{q.subtitle}</div>
      <div className="mt-4 overflow-hidden rounded-xl bg-white text-[15px] md:text-[16px]">
        {opts.map((label, i) => (
          <div
            key={label}
            className={`flex min-h-12 items-center justify-between px-3.5 ${i < opts.length - 1 ? "border-b border-line" : ""}`}
          >
            <span>{label}</span>
            {i === 1 && <Check className="h-4 w-4 text-tint" />}
          </div>
        ))}
      </div>
      <div className="mt-4 flex min-h-12 items-center justify-center rounded-[14px] bg-tint text-[16px] font-medium text-white">
        次へ
      </div>
    </div>
  );
}

/* ───────── お悩み ───────── */
function Problems() {
  return (
    <section className="border-t border-line bg-fill py-16 md:py-24">
      <Container>
        <SectionTitle
          title="防犯カメラ選び、こんなことで迷っていませんか。"
          lead="これまでの導入は、選ぶところから設置のあとまで、手間と迷いがつきものでした。"
        />
        <div className="mt-10 grid gap-3 md:grid-cols-2 md:gap-4">
          {PROBLEMS.map((p) => (
            <div key={p.title} className="rounded-[18px] bg-white p-6 md:p-7">
              <h3 className="text-[19px] font-bold">{p.title}</h3>
              <p className="mt-2 text-[15px] leading-[1.8] text-body">{p.text}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ───────── ご利用の流れ ───────── */
function Flow() {
  return (
    <section id="flow" className="bg-white py-16 md:py-24">
      <Container>
        <SectionTitle
          title="ご利用の流れ"
          lead="診断で候補を決めたら、あとはご連絡いただくだけ。現地調査から設置まで、窓口はひとつです。"
        />
        <ol className="mt-10 grid gap-x-10 md:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((s, i) => (
            <li key={s.title} className="flex gap-4 border-t border-line py-6">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-fill text-[15px] font-semibold text-tint">
                {i + 1}
              </span>
              <div>
                <h3 className="text-[17px] font-bold leading-8">{s.title}</h3>
                <p className="mt-1 text-[15px] leading-[1.8] text-body">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </Container>
    </section>
  );
}

/* ───────── 取り扱いメーカー ───────── */
function Makers() {
  const { brandMap } = useBrandMap();
  const { products, ready } = useProducts();
  const entries = Object.entries(brandMap);
  if (entries.length === 0) return null;
  const countFor = (name) => products.filter((p) => p.makerName === name).length;

  return (
    <section className="border-t border-line bg-fill py-16 md:py-24">
      <Container>
        <div className="flex items-end justify-between gap-4">
          <SectionTitle title="取り扱いメーカー" lead="掲載している商品と、メーカーが公表している仕様を確認できます。" />
        </div>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {entries.map(([slug, name]) => (
            <Link
              key={slug}
              to={`/brands/${slug}`}
              className="group flex min-h-[72px] items-center justify-between rounded-[14px] bg-white px-5 py-4 transition-colors hover:bg-white/70"
            >
              <span>
                <span className="block text-[17px] font-semibold">{name}</span>
                {ready && <span className="block text-[13px] text-ink-2">{countFor(name)}機種</span>}
              </span>
              <Chevron className="text-chevron transition-transform group-hover:translate-x-0.5" />
            </Link>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ───────── 最後の案内 ───────── */
function Closing() {
  return (
    <section className="bg-white py-20 md:py-28">
      <Container className="text-center">
        <SectionTitle
          center
          title="まずは、診断から。"
          lead={`${QUESTION_COUNT}つの質問に直感で答えるだけ。登録や入力は必要ありません。`}
        />
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-7">
          <Button to="/wizard" className="!min-h-[50px] !px-7">
            無料で診断する
          </Button>
          <Link to="/contact" className="flex min-h-11 items-center gap-1 text-[17px] text-tint hover:text-tint-dark">
            お問い合わせ
            <Chevron className="h-3 w-2" />
          </Link>
        </div>
      </Container>
    </section>
  );
}
